import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ScheduleByIdSessionRequest,
  WeeklyScheduleModel,
  WeeklyScheduleRequest,
  GenerateTracksRequest,
  ValidationReport,
  AIStatus,
  AddUserToScheduleRequest,
  GenerateScheduleResponse,
  TransferLearnerRequest,
  TransferLearnerResult,
  WeeklyScheduleLearnerModel,
} from '@tsin-core/models/schedule.model';
import { environment } from '@tsin-env/environment';
import { Observable } from 'rxjs';

const SCHEDULE_URL = `${environment.learningBaseUrl}/schedules`;

@Injectable({
  providedIn: 'root',
})

export class ScheduleService {

  http: HttpClient = inject(HttpClient);

  createSchedule(payload: any) {
    return this.http.post(SCHEDULE_URL, payload);
  }

  getSchedule() {
    return this.http.get<any[]>(SCHEDULE_URL);
  }

  createWeeklySchedule(payload: Omit<WeeklyScheduleRequest, 'id'>) {
    return this.http.post(SCHEDULE_URL + `/weekly`, payload);
  }

  getWeeklySchedule(): Observable<WeeklyScheduleModel[]> {
    return this.http.get<WeeklyScheduleModel[]>(SCHEDULE_URL + `/weekly`);
  }

  getWeeklyScheduleByLearning(learningId: string) {
    return this.http.get(SCHEDULE_URL + `/weekly/learning/${learningId}`);
  }

  updateMemberGroup(groupName: string, user: any) {
    return this.http.put(SCHEDULE_URL + `/members`, { ...user, group: groupName, });
  }

  updateMemberTrack(groupName: string, track: string, user: any) {
    return this.http.put(SCHEDULE_URL + `/members`, { ...user, group: groupName, track: track });
  }

  updateLeaderGroup(user: any) {
    return this.http.put(SCHEDULE_URL + `/leaders`, user);
  }

  updateWeeklySchedule(payload: any) {
    return this.http.put(SCHEDULE_URL + `/weekly`, payload);
  }

  getSingleWeeklySchedule(id: any) {
    return this.http.get(SCHEDULE_URL + `/weekly/single/${id}`);
  }

  getScheduleByIdSession(payload: ScheduleByIdSessionRequest): Observable<any> {
    return this.http.get(SCHEDULE_URL + `/weekly/single/${payload.id}/${payload.sessionId}`);
  }

  deleteSingleWeeklySchedule(id: any) {
    return this.http.delete(SCHEDULE_URL + `/weekly/single/${id}`);
  }

  addWeeklySchedule(payload: any) {
    return this.http.post(SCHEDULE_URL + `/weekly/add-schedule`, payload);
  }

  removeWeeklySchedule(payload: any) {
    return this.http.delete(SCHEDULE_URL + `/weekly/remove-schedule`, payload);
  }

  generateTracks(payload: any) {
    return this.http.post(SCHEDULE_URL + `/generate-tracks`, payload);
  }

  getSingleSchedule(id: any) {
    return this.http.get(SCHEDULE_URL + `/single/${id}`);
  }

  deleteSingleSchedule(id: any) {
    return this.http.get(SCHEDULE_URL + `/single/${id}`);
  }

  getLeaders(learningId: any) {
    return this.http.get(SCHEDULE_URL + `/leaders/${learningId}`);
  }

  getSimulationAllocationExportData(weeklyScheduleId: string) {
    return this.http.get(SCHEDULE_URL + `/export-schedule/${weeklyScheduleId}`);
  }

  // ============================================================================
  // NEW METHODS FOR ALGORITHM UPDATE & AI/ML
  // ============================================================================

  /**
   * Generate tracks with override and AI options
   */
  generateTracksWithOptions(payload: GenerateTracksRequest): Observable<GenerateScheduleResponse> {
    return this.http.post<GenerateScheduleResponse>(SCHEDULE_URL + `/generate-tracks`, payload);
  }

  /**
   * Validate a weekly schedule
   */
  validateSchedule(weeklyScheduleId: string): Observable<ValidationReport> {
    return this.http.get<ValidationReport>(SCHEDULE_URL + `/weekly/${weeklyScheduleId}/validate`);
  }

  /**
   * Get AI/ML model status
   */
  getAIStatus(): Observable<AIStatus> {
    return this.http.get<AIStatus>(SCHEDULE_URL + `/ai/status`);
  }

  /**
   * Collect feedback for AI/ML learning
   */
  collectFeedback(weeklyScheduleId: string): Observable<any> {
    return this.http.post(SCHEDULE_URL + `/weekly/${weeklyScheduleId}/feedback`, {});
  }

  /**
   * Add user to schedule with placement options
   */
  addUserToSchedule(payload: AddUserToScheduleRequest): Observable<any> {
    return this.http.post(SCHEDULE_URL + `/add-session-user`, payload);
  }

  /**
   * Transfer a learner from one weekly schedule to another.
   * The leadership repair algorithm re-runs on the target schedule.
   *
   * Transférer un apprenant vers un autre horaire hebdomadaire.
   * L'algorithme de réparation du leadership se relance sur l'horaire cible.
   */
  transferLearner(payload: TransferLearnerRequest): Observable<TransferLearnerResult> {
    return this.http.post<TransferLearnerResult>(SCHEDULE_URL + `/transfer-learner`, payload);
  }

  /**
   * Returns all unique learners enrolled in a weekly schedule.
   * Retourne tous les apprenants inscrits dans un horaire hebdomadaire.
   */
  getLearnersForSchedule(weeklyScheduleId: string): Observable<{ data: WeeklyScheduleLearnerModel[]; total: number }> {
    return this.http.get<{ data: WeeklyScheduleLearnerModel[]; total: number }>(
      `${SCHEDULE_URL}/weekly/${weeklyScheduleId}/learners`,
    );
  }
}